/*
    🔥 REGISTRO DE ATAJOS

    ⚠️ EL PROBLEMA QUE RESUELVE

    En estas entregas quedaron repartidos Ctrl+K (paleta), Ctrl+S
    (guardar), Escape (cerrar), flechas (árbol, visor, tour, tabla),
    Ctrl+Enter (comentarios) y Enter (chat). Todos funcionan y NINGUNO
    está a la vista: nadie descubre Ctrl+K por casualidad.

    Acá se declaran una vez y de ahí salen tanto la ayuda como, si
    querés, el registro real del listener.

    ⚠️ LA TECLA SE ESCRIBE, NO SE DIBUJA

    'mod+k' se traduce a ⌘K en Mac y a Ctrl+K en el resto. Escribir
    "Ctrl + K" a mano deja mintiendo la ayuda en la mitad de las
    computadoras.
*/

export const IS_APPLE = typeof navigator !== 'undefined'
    && /Mac|iPod|iPhone|iPad/.test(navigator.platform ?? navigator.userAgent ?? '');

const TOKEN_LABEL = {
    mod: IS_APPLE ? '⌘' : 'Ctrl',
    ctrl: IS_APPLE ? '⌃' : 'Ctrl',
    alt: IS_APPLE ? '⌥' : 'Alt',
    shift: IS_APPLE ? '⇧' : 'Shift',
    enter: '↵',
    esc: 'Esc',
    escape: 'Esc',
    space: 'Espacio',
    up: '↑',
    down: '↓',
    left: '←',
    right: '→',
    tab: 'Tab',
    del: 'Supr',
    backspace: '⌫',
    plus: '+',
    minus: '−',
};

// 🔥 'mod+shift+k' -> ['⌘', '⇧', 'K']
export const onParseKeys = (combo) => String(combo ?? '')
    .split('+')
    .map((raw) => {
        const T = raw.trim().toLowerCase();
        return TOKEN_LABEL[T] ?? (T.length === 1 ? T.toUpperCase() : raw.trim());
    });

// 🔥 ¿Este evento corresponde a este combo?
export const onMatchCombo = (event, combo) => {

    const PARTS = String(combo ?? '').toLowerCase().split('+').map((p) => p.trim());

    const KEY = PARTS[PARTS.length - 1];

    const NEEDS_MOD = PARTS.includes('mod');
    const NEEDS_CTRL = PARTS.includes('ctrl');
    const NEEDS_ALT = PARTS.includes('alt');
    const NEEDS_SHIFT = PARTS.includes('shift');

    const HAS_MOD = IS_APPLE ? event.metaKey : event.ctrlKey;

    if (NEEDS_MOD !== HAS_MOD) return false;
    if (NEEDS_CTRL && !event.ctrlKey) return false;
    if (NEEDS_ALT !== event.altKey) return false;
    if (NEEDS_SHIFT !== event.shiftKey) return false;

    const PRESSED = event.key.toLowerCase();

    const ALIASES = { escape: 'esc', arrowup: 'up', arrowdown: 'down', arrowleft: 'left', arrowright: 'right', ' ': 'space' };

    return (ALIASES[PRESSED] ?? PRESSED) === KEY;
};

/*
    🔥 CATÁLOGO POR DEFECTO

    Refleja lo que la template YA hace. Cada proyecto le suma los suyos
    con onBuildShortcuts.
*/
export const SHORTCUTS_DEFAULT = [

    // -------------------- 🎯 GLOBALES
    { keys: 'mod+k', label: 'Abrir la paleta de comandos', group: 'General' },
    { keys: 'shift+/', label: 'Mostrar esta ayuda', group: 'General' },
    { keys: 'esc', label: 'Cerrar lo que esté abierto', group: 'General' },

    // -------------------- 🎯 EDICIÓN
    { keys: 'mod+s', label: 'Guardar cambios', group: 'Edición' },
    { keys: 'mod+enter', label: 'Enviar un comentario', group: 'Edición' },
    { keys: 'enter', label: 'Enviar un mensaje de chat', group: 'Edición' },
    { keys: 'shift+enter', label: 'Salto de línea en el chat', group: 'Edición' },

    // -------------------- 🎯 NAVEGACIÓN
    { keys: 'up', label: 'Elemento anterior', group: 'Navegación' },
    { keys: 'down', label: 'Elemento siguiente', group: 'Navegación' },
    { keys: 'right', label: 'Abrir carpeta / imagen siguiente', group: 'Navegación' },
    { keys: 'left', label: 'Cerrar carpeta / imagen anterior', group: 'Navegación' },

    // -------------------- 🎯 VISOR
    { keys: 'plus', label: 'Acercar', group: 'Visor de imágenes' },
    { keys: 'minus', label: 'Alejar', group: 'Visor de imágenes' },
    { keys: '0', label: 'Volver al tamaño original', group: 'Visor de imágenes' },
];

export const onBuildShortcuts = (extra = [], { base = SHORTCUTS_DEFAULT } = {}) => [...base, ...extra];
